import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../../../../store';
import { 
  fetchScheduleSettingsForecastDaily, 
  // fetchScheduleSettingsForecastDailyNONDSD, // Commented out - UI table is disabled
  fetchScheduleSettingsActualDaily, 
  // fetchScheduleSettingsActualDailyNONDSD, // Commented out - UI table is disabled 
  fetchScheduleSettingsForecastZoneDistributionDSD, 
  fetchScheduleSettingsForecastZoneDistributionNONDSD, 
  fetchScheduleSettingsActualZoneDistributionDSD, 
  fetchScheduleSettingsActualZoneDistributionNONDSD, 
  fetchScheduleSettingsHistoricalHours, 
  fetchShiftTimes, 
  fetchScheduleForecast,
  cleanScheduleSettings 
} from '../../../../../store/thunks/dloThunks';
import { 
  setForecastDSDTableData, 
  // setForecastNONDSDTableData, // Commented out - UI table is disabled
  setActualDSDTableData, 
  // setActualNONDSDTableData, // Commented out - UI table is disabled 
  setForecastDSDZoneTableData, 
  setForecastNONDSDZoneTableData, 
  setActualDSDZoneTableData, 
  setActualNONDSDZoneTableData, 
  setHistoricalHoursTableData, 
  setHistoricalHoursUnsavedChanges, 
  setShiftTimesLoading, 
  setShiftTimesData, 
  setShiftTimesError, 
  setScheduleForecastLoading, 
  setScheduleForecastData, 
  setScheduleForecastTableData, 
  setScheduleForecastError,
  setCleanScheduleSettingsLoading,
  setCleanScheduleSettingsData,
  setCleanScheduleSettingsError
} from '../../../../../store/slices/dloSlice';
import { transformDailyPercentageData, transformDailyCasesData, transformZoneDistributionData, transformHistoricalHoursData, transformScheduleForecastData } from '../utils/dataTransformers';

export const useDLOScheduleData = (scheduleType: 'new_schedule' | 'existing_schedule' = 'new_schedule') => {
  const dispatch = useAppDispatch();
  
  // Track loading state for all initial data fetches
  const [isAllDataLoaded, setIsAllDataLoaded] = useState(false);
  
  // Get all data from Redux using a single selector
  const {
    selectedLocation,
    dloScheduleSetting,
    forecastDSDTableData,
    forecastNONDSDTableData,
    actualDSDTableData,
    actualNONDSDTableData,
    forecastDSDZoneTableData,
    forecastNONDSDZoneTableData,
    actualDSDZoneTableData,
    actualNONDSDZoneTableData,
    historicalHoursTableData,
    historicalHoursData,
    shiftTimesData,
    shiftTimesTableData,
    scheduleForecastData,
    scheduleForecastTableData,
    createSchedulePeriod,
    createScheduleWeek,
    createScheduleYear,
    cleanScheduleSettingsData,
    cleanScheduleSettingsLoading,
    cleanScheduleSettingsError
  } = useSelector((state: RootState) => ({
    selectedLocation: state.appData.selectedLocation,
    dloScheduleSetting: state.dlo.scheduleSetting,
    forecastDSDTableData: state.dlo.scheduleSetting.forecastDSDTableData,
    forecastNONDSDTableData: state.dlo.scheduleSetting.forecastNONDSDTableData,
    actualDSDTableData: state.dlo.scheduleSetting.actualDSDTableData,
    actualNONDSDTableData: state.dlo.scheduleSetting.actualNONDSDTableData,
    forecastDSDZoneTableData: state.dlo.scheduleSetting.forecastDSDZoneTableData,
    forecastNONDSDZoneTableData: state.dlo.scheduleSetting.forecastNONDSDZoneTableData,
    actualDSDZoneTableData: state.dlo.scheduleSetting.actualDSDZoneTableData,
    actualNONDSDZoneTableData: state.dlo.scheduleSetting.actualNONDSDZoneTableData,
    historicalHoursTableData: state.dlo.scheduleSetting.historicalHoursTableData,
    historicalHoursData: state.dlo.scheduleSetting.scheduleSettingsHistoricalHours,
    shiftTimesData: state.dlo.scheduleSetting.shiftTimes,
    shiftTimesTableData: state.dlo.scheduleSetting.shiftTimesTableData,
    scheduleForecastData: state.dlo.scheduleSetting.scheduleForecast,
    scheduleForecastTableData: state.dlo.scheduleSetting.scheduleForecastTableData,
    createSchedulePeriod: state.dlo.createSchedule.period,
    createScheduleWeek: state.dlo.createSchedule.week,
    createScheduleYear: state.dlo.createSchedule.year,
    cleanScheduleSettingsData: state.dlo.scheduleSetting.cleanScheduleSettingsData,
    cleanScheduleSettingsLoading: state.dlo.scheduleSetting.cleanScheduleSettingsLoading,
    cleanScheduleSettingsError: state.dlo.scheduleSetting.cleanScheduleSettingsError
  }));
  
  // Function to fetch all data after cleanScheduleSettings or directly for existing schedules
  const fetchAllScheduleData = () => {
    setIsAllDataLoaded(false);
    
    // Fetch schedule forecast data
    dispatch(setScheduleForecastLoading(true));
    const scheduleForecastPromise = dispatch(fetchScheduleForecast()).then((forecastResult: any) => {
      console.log('Schedule Forecast API result:', forecastResult);

      if (fetchScheduleForecast.fulfilled.match(forecastResult)) {
        console.log('Schedule Forecast API success, payload:', forecastResult.payload);
        dispatch(setScheduleForecastData(forecastResult.payload));
      } else {
        console.log('Schedule Forecast API error:', forecastResult.payload);
        const errorMessage = forecastResult.payload?.error || forecastResult.payload?.message || 
                           (typeof forecastResult.payload === 'string' ? forecastResult.payload : 'Failed to fetch schedule forecast');
        dispatch(setScheduleForecastError(errorMessage));
        return Promise.reject(errorMessage); // Return rejected promise instead of throwing
      }

      // Sequential chain for DSD-related data with error handling
      const dsdChainPromise = dispatch(fetchScheduleSettingsForecastZoneDistributionDSD()).then((dsdZoneResult: any) => {
        if (!fetchScheduleSettingsForecastZoneDistributionDSD.fulfilled.match(dsdZoneResult)) {
          const errorMessage = dsdZoneResult.payload?.error || dsdZoneResult.payload?.message || 'Failed to fetch DSD zone distribution forecast';
          dispatch(setScheduleForecastError(errorMessage));
          return Promise.reject(errorMessage);
        }
        return dispatch(fetchScheduleSettingsForecastDaily()).then((dsdDailyResult: any) => {
          if (!fetchScheduleSettingsForecastDaily.fulfilled.match(dsdDailyResult)) {
            const errorMessage = dsdDailyResult.payload?.error || dsdDailyResult.payload?.message || 'Failed to fetch DSD forecast daily';
            dispatch(setScheduleForecastError(errorMessage));
            return Promise.reject(errorMessage);
          }
          return dispatch(fetchScheduleSettingsActualZoneDistributionDSD()).then((actualZoneResult: any) => {
            if (!fetchScheduleSettingsActualZoneDistributionDSD.fulfilled.match(actualZoneResult)) {
              const errorMessage = actualZoneResult.payload?.error || actualZoneResult.payload?.message || 'Failed to fetch DSD actual zone distribution';
              dispatch(setScheduleForecastError(errorMessage));
              return Promise.reject(errorMessage);
            }
            return dispatch(fetchScheduleSettingsActualDaily()).then((actualDailyResult: any) => {
              if (!fetchScheduleSettingsActualDaily.fulfilled.match(actualDailyResult)) {
                const errorMessage = actualDailyResult.payload?.error || actualDailyResult.payload?.message || 'Failed to fetch DSD actual daily';
                dispatch(setScheduleForecastError(errorMessage));
                return Promise.reject(errorMessage);
              }
            });
          });
        });
      });

      // Parallel chain for NON-DSD data with error handling
      const nonDsdChainPromise = dispatch(fetchScheduleSettingsForecastZoneDistributionNONDSD()).then((nonDsdZoneResult: any) => {
        if (!fetchScheduleSettingsForecastZoneDistributionNONDSD.fulfilled.match(nonDsdZoneResult)) {
          const errorMessage = nonDsdZoneResult.payload?.error || nonDsdZoneResult.payload?.message || 'Failed to fetch NON-DSD zone distribution forecast';
          dispatch(setScheduleForecastError(errorMessage));
          return Promise.reject(errorMessage);
        }
        return dispatch(fetchScheduleSettingsActualZoneDistributionNONDSD()).then((nonDsdActualResult: any) => {
          if (!fetchScheduleSettingsActualZoneDistributionNONDSD.fulfilled.match(nonDsdActualResult)) {
            const errorMessage = nonDsdActualResult.payload?.error || nonDsdActualResult.payload?.message || 'Failed to fetch NON-DSD actual zone distribution';
            dispatch(setScheduleForecastError(errorMessage));
            return Promise.reject(errorMessage);
          }
        });
      });

      // Wait for both DSD and NON-DSD chains to complete
      return Promise.all([dsdChainPromise, nonDsdChainPromise]);
    }).catch((error) => {
      // Catch any errors from the API chains and store them - DO NOT re-throw
      console.error('Error in fetchAllScheduleData:', error);
      const errorMessage = typeof error === 'string' ? error : (error?.message || 'An error occurred while fetching schedule data');
      dispatch(setScheduleForecastError(errorMessage));
      // Return resolved promise to prevent Error Boundary
      return Promise.resolve();
    });
    
    // Fetch historical hours data
    const historicalHoursPromise = dispatch(fetchScheduleSettingsHistoricalHours()).then((histResult: any) => {
      if (!fetchScheduleSettingsHistoricalHours.fulfilled.match(histResult)) {
        const errorMessage = histResult.payload?.error || histResult.payload?.message || 'Failed to fetch historical hours';
        dispatch(setScheduleForecastError(errorMessage));
        return Promise.reject(errorMessage);
      }
    }).catch((error) => {
      console.error('Error fetching historical hours:', error);
      const errorMessage = typeof error === 'string' ? error : (error?.message || 'Failed to fetch historical hours');
      dispatch(setScheduleForecastError(errorMessage));
      // Return resolved promise to prevent Error Boundary
      return Promise.resolve();
    });
    
    // Fetch shift times data
    dispatch(setShiftTimesLoading(true));
    const shiftTimesPromise = dispatch(fetchShiftTimes()).then((shiftResult: any) => {
      console.log('Shift Times API result:', shiftResult);
      if (fetchShiftTimes.fulfilled.match(shiftResult)) {
        console.log('Shift Times API success, payload:', shiftResult.payload);
        dispatch(setShiftTimesData(shiftResult.payload));
      } else {
        console.log('Shift Times API error:', shiftResult.payload);
        const errorMessage = shiftResult.payload?.error || shiftResult.payload?.message || 
                           (typeof shiftResult.payload === 'string' ? shiftResult.payload : 'Failed to fetch shift times');
        dispatch(setShiftTimesError(errorMessage));
        return Promise.reject(errorMessage);
      }
    }).catch((error) => {
      console.error('Error fetching shift times:', error);
      const errorMessage = typeof error === 'string' ? error : (error?.message || 'Failed to fetch shift times');
      dispatch(setShiftTimesError(errorMessage));
      // Return resolved promise to prevent Error Boundary
      return Promise.resolve();
    });

    // Wait for all dispatch chains to complete before setting isAllDataLoaded to true
    Promise.all([scheduleForecastPromise, historicalHoursPromise, shiftTimesPromise])
      .then(() => {
        setIsAllDataLoaded(true);
      })
      .catch((error) => {
        // This should rarely be hit now since we're catching errors above
        console.error('Error loading schedule data:', error);
        setIsAllDataLoaded(false);
      });
  };

  // Fetch initial data - conditionally call cleanScheduleSettings based on scheduleType
  useEffect(() => {
    if (selectedLocation?.id && createSchedulePeriod && createScheduleWeek) {
      const currentYear = new Date().getFullYear();
      
      if (scheduleType === 'new_schedule') {
        // For new schedules, first call cleanScheduleSettings, then fetch all other data
        dispatch(setCleanScheduleSettingsLoading(true));
        dispatch(cleanScheduleSettings({
          locId: selectedLocation.id,
          period: createSchedulePeriod,
          week: createScheduleWeek,
          year: createScheduleYear || currentYear
        })).then((result: any) => {
          console.log('Clean Schedule Settings API result:', result);
          if (cleanScheduleSettings.fulfilled.match(result)) {
            console.log('Clean Schedule Settings API success, payload:', result.payload);
            dispatch(setCleanScheduleSettingsData(result.payload));
            
            // After successful cleanup, fetch all other data
            fetchAllScheduleData();
          } else {
            console.log('Clean Schedule Settings API error:', result.payload);
            // Extract error message from the API response
            const errorMessage = result.payload?.error || result.payload?.message || 
                                 (typeof result.payload === 'string' ? result.payload : 'Failed to clean schedule settings');
            dispatch(setCleanScheduleSettingsError(errorMessage));
          }
        }).catch((error) => {
          // Additional catch to prevent any uncaught errors from reaching Error Boundary
          console.error('Unexpected error in cleanScheduleSettings:', error);
          const errorMessage = error?.message || 'An unexpected error occurred while cleaning schedule settings';
          dispatch(setCleanScheduleSettingsError(errorMessage));
        });
      } else {
        // For existing schedules, directly fetch all data without cleanScheduleSettings
        console.log('Existing schedule detected, skipping cleanScheduleSettings and fetching data directly');
        fetchAllScheduleData();
      }
    }
  }, [dispatch, selectedLocation?.id, createSchedulePeriod, createScheduleWeek, scheduleType]);

  // Initialize table data when API data is loaded
  useEffect(() => {
    if (dloScheduleSetting.scheduleSettingsForecastDaily?.data && forecastDSDTableData.length === 0) {
      const transformedData = transformDailyPercentageData(dloScheduleSetting.scheduleSettingsForecastDaily.data);
      dispatch(setForecastDSDTableData(transformedData));
    }
  }, [dloScheduleSetting.scheduleSettingsForecastDaily?.data, forecastDSDTableData.length, dispatch]);

  // Commented out - UI table is disabled for NON-DSD weekly percentage
  // useEffect(() => {
  //   if (dloScheduleSetting.scheduleSettingsForecastDailyNONDSD?.data && forecastNONDSDTableData.length === 0) {
  //     const transformedData = transformDailyPercentageData(dloScheduleSetting.scheduleSettingsForecastDailyNONDSD.data);
  //     dispatch(setForecastNONDSDTableData(transformedData));
  //   }
  // }, [dloScheduleSetting.scheduleSettingsForecastDailyNONDSD?.data, forecastNONDSDTableData.length, dispatch]);

  useEffect(() => {
    if (dloScheduleSetting.scheduleSettingsActualDaily?.data && actualDSDTableData.length === 0) {
      const transformedData = transformDailyCasesData(dloScheduleSetting.scheduleSettingsActualDaily.data);
      dispatch(setActualDSDTableData(transformedData));
    }
  }, [dloScheduleSetting.scheduleSettingsActualDaily?.data, actualDSDTableData.length, dispatch]);

  // Commented out - UI table is disabled for NON-DSD weekly percentage
  // useEffect(() => {
  //   if (dloScheduleSetting.scheduleSettingsActualDailyNONDSD?.data && actualNONDSDTableData.length === 0) {
  //     const transformedData = transformDailyPercentageData(dloScheduleSetting.scheduleSettingsActualDailyNONDSD.data);
  //     dispatch(setActualNONDSDTableData(transformedData));
  //   }
  // }, [dloScheduleSetting.scheduleSettingsActualDailyNONDSD?.data, actualNONDSDTableData.length, dispatch]);

  // Initialize zone distribution table data
  useEffect(() => {
    if (dloScheduleSetting.scheduleSettingsForecastZoneDistributionDSD?.data) {
      const transformedData = transformZoneDistributionData(dloScheduleSetting.scheduleSettingsForecastZoneDistributionDSD.data, 'DSD');
      dispatch(setForecastDSDZoneTableData(transformedData));
    }
  }, [dloScheduleSetting.scheduleSettingsForecastZoneDistributionDSD?.data, dispatch]);

  useEffect(() => {
    if (dloScheduleSetting.scheduleSettingsForecastZoneDistributionNONDSD?.data) {
      const transformedData = transformZoneDistributionData(dloScheduleSetting.scheduleSettingsForecastZoneDistributionNONDSD.data, 'NON-DSD');
      dispatch(setForecastNONDSDZoneTableData(transformedData));
    }
  }, [dloScheduleSetting.scheduleSettingsForecastZoneDistributionNONDSD?.data, dispatch]);

  useEffect(() => {
    if (dloScheduleSetting.scheduleSettingsActualZoneDistributionDSD?.data) {
      const transformedData = transformZoneDistributionData(dloScheduleSetting.scheduleSettingsActualZoneDistributionDSD.data, 'DSD');
      dispatch(setActualDSDZoneTableData(transformedData));
    }
  }, [dloScheduleSetting.scheduleSettingsActualZoneDistributionDSD?.data, dispatch]);

  useEffect(() => {
    if (dloScheduleSetting.scheduleSettingsActualZoneDistributionNONDSD?.data) {
      const transformedData = transformZoneDistributionData(dloScheduleSetting.scheduleSettingsActualZoneDistributionNONDSD.data, 'NON-DSD');
      dispatch(setActualNONDSDZoneTableData(transformedData));
    }
  }, [dloScheduleSetting.scheduleSettingsActualZoneDistributionNONDSD?.data, dispatch]);

  // Initialize historical hours table data
  useEffect(() => {
    if (historicalHoursData?.data && historicalHoursTableData.length === 0) {
      const transformedData = transformHistoricalHoursData(historicalHoursData.data);
      dispatch(setHistoricalHoursTableData(transformedData));
      dispatch(setHistoricalHoursUnsavedChanges({}));
    }
  }, [historicalHoursData?.data, historicalHoursTableData.length, dispatch]);

  // Initialize schedule forecast table data
  useEffect(() => {
    if (scheduleForecastData?.data && scheduleForecastTableData.length === 0) {
      const transformedData = transformScheduleForecastData(scheduleForecastData.data);
      dispatch(setScheduleForecastTableData(transformedData));
    }
  }, [scheduleForecastData?.data, scheduleForecastTableData.length, dispatch]);

  return {
    selectedLocation,
    dloScheduleSetting,
    forecastDSDTableData,
    forecastNONDSDTableData,
    actualDSDTableData,
    actualNONDSDTableData,
    forecastDSDZoneTableData,
    forecastNONDSDZoneTableData,
    actualDSDZoneTableData,
    actualNONDSDZoneTableData,
    historicalHoursTableData,
    historicalHoursData,
    shiftTimesData,
    shiftTimesTableData,
    scheduleForecastData,
    scheduleForecastTableData,
    cleanScheduleSettingsData,
    cleanScheduleSettingsLoading,
    cleanScheduleSettingsError,
    createSchedulePeriod,
    createScheduleWeek,
    createScheduleYear,
    isAllDataLoaded,
  };
};
